import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { BasketContext } from '../contexts/BasketContext';
import { buildApiUrl } from '../config/api';

const CATEGORY_LABELS = {
    specials: 'Today\'s specials',
    'quick bites': 'Quick bites',
    mains: 'Main meals',
    desserts: 'Desserts',
    sides: 'Sides',
    uncategorized: 'More to try',
};
const PREFERRED_CATEGORY_ORDER = ['specials', 'quick bites', 'mains', 'desserts', 'sides'];
const formatMoney = value => `R${Number(value || 0).toFixed(2)}`;

function flattenVariants(variants) {
    if (Array.isArray(variants)) return variants;
    return variants && typeof variants === 'object' ? Object.values(variants).flat() : [];
}

function imageUrl(image) {
    if (!image) return `${process.env.PUBLIC_URL}/curry.jpg`;
    return /^https?:\/\//i.test(image) ? image : `${process.env.PUBLIC_URL}/${image.replace(/^\/+/, '')}`;
}

function Menu() {
    const [menuItems, setMenuItems] = useState([]);
    const [activeCategory, setActiveCategory] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [basketNotice, setBasketNotice] = useState('');
    const { addToBasket, removeSoldOutItems } = useContext(BasketContext);

    useEffect(() => {
        let active = true;
        fetch(buildApiUrl('/api/menu'))
            .then(response => {
                if (!response.ok) throw new Error('The menu is unavailable right now.');
                return response.json();
            })
            .then(data => {
                if (!active) return;
                const items = Array.isArray(data) ? data : [];
                const removedItems = removeSoldOutItems(items);
                if (removedItems.length) {
                    setBasketNotice(`${removedItems.map(item => item.name).join(', ')} ${removedItems.length === 1 ? 'was' : 'were'} removed from your basket because ${removedItems.length === 1 ? 'it is' : 'they are'} sold out.`);
                }
                setMenuItems(items);
            })
            .catch(requestError => active && setError(requestError.message))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [removeSoldOutItems]);

    const categories = useMemo(() => {
        const present = menuItems.reduce((set, item) => set.add(item.category || 'uncategorized'), new Set());
        return [...PREFERRED_CATEGORY_ORDER, ...Array.from(present)]
            .filter((category, index, all) => present.has(category) && all.indexOf(category) === index);
    }, [menuItems]);

    useEffect(() => {
        if (!activeCategory && categories.length) setActiveCategory(categories[0]);
    }, [activeCategory, categories]);

    const visibleItems = menuItems.filter(item => (item.category || 'uncategorized') === activeCategory);

    const openItem = item => {
        if (item.isSoldOut) return;
        const variants = flattenVariants(item.variants);
        if (!variants.length) {
            addToBasket(item);
            return;
        }
        setSelectedItem(item);
        setSelectedVariant(variants.length === 1 ? variants[0] : null);
    };

    const confirmSelection = () => {
        if (!selectedItem) return;
        const variants = flattenVariants(selectedItem.variants);
        if (variants.length && !selectedVariant) return;
        addToBasket(selectedItem, selectedVariant);
        setSelectedItem(null);
        setSelectedVariant(null);
    };

    return (
        <section className="menu-section" id="menu">
            <div className="section-heading">
                <p className="eyebrow">Order online</p>
                <h1>Made fresh for your table.</h1>
                <p>Choose a dish, select an option, and place your order when you are ready.</p>
            </div>

            {loading && <div className="menu-state">Loading the menu...</div>}
            {error && <div className="menu-state menu-state--error">{error}</div>}
            {basketNotice && <div className="menu-notice" role="status">{basketNotice}</div>}
            {!loading && !error && !menuItems.length && <div className="menu-state">No menu items are available yet.</div>}

            {!!categories.length && (
                <div className="menu-categories" role="tablist" aria-label="Menu categories">
                    {categories.map(category => (
                        <button type="button" role="tab" aria-selected={activeCategory === category} className={activeCategory === category ? 'is-selected' : ''} key={category} onClick={() => setActiveCategory(category)}>
                            {CATEGORY_LABELS[category] || category}
                        </button>
                    ))}
                </div>
            )}

            <div className="menu-grid" role="tabpanel">
                {visibleItems.map(item => {
                    const variants = flattenVariants(item.variants);
                    const prices = variants.length ? variants.map(variant => Number(variant.price)) : [Number(item.price)];
                    const fromPrice = Math.min(...prices);
                    return (
                        <article className={`food-card ${item.isSoldOut ? 'food-card--sold-out' : ''}`} key={item.id}>
                            {item.isSoldOut && <span className="sold-out-badge">Sold out</span>}
                            <img src={imageUrl(item.image)} alt={item.name} />
                            <div className="food-card__body">
                                <div><h2>{item.name}</h2><p>{item.description}</p></div>
                                <div className="food-card__footer">
                                    <span>{variants.length ? 'From ' : ''}<strong>{formatMoney(fromPrice)}</strong></span>
                                    <button type="button" onClick={() => openItem(item)} disabled={item.isSoldOut} aria-label={item.isSoldOut ? `${item.name} is sold out` : `Add ${item.name} to basket`}><FaPlus /> <span>{item.isSoldOut ? 'Sold out' : 'Add'}</span></button>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>

            {selectedItem && (
                <div className="dialog-backdrop" role="presentation" onMouseDown={() => setSelectedItem(null)}>
                    <section className="option-dialog" role="dialog" aria-modal="true" aria-labelledby="option-dialog-title" onMouseDown={event => event.stopPropagation()}>
                        <button className="dialog-close" type="button" onClick={() => setSelectedItem(null)} aria-label="Close options"><FaTimes /></button>
                        <p className="eyebrow">Choose your option</p><h2 id="option-dialog-title">{selectedItem.name}</h2><p>{selectedItem.description}</p>
                        <div className="option-list">
                            {flattenVariants(selectedItem.variants).map(variant => (
                                <label className={selectedVariant?.id === variant.id ? 'option-row is-selected' : 'option-row'} key={variant.id}>
                                    <input type="radio" name="menu-option" checked={selectedVariant?.id === variant.id} onChange={() => setSelectedVariant(variant)} />
                                    <span><b>{variant.name}</b></span><strong>{formatMoney(variant.price)}</strong>
                                </label>
                            ))}
                        </div>
                        <button className="button button--primary button--wide" type="button" disabled={!selectedVariant} onClick={confirmSelection}>Add to basket</button>
                    </section>
                </div>
            )}
        </section>
    );
}

export default Menu;
